import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Landmark, CreditCard, ChevronDown, Copy, Check, Clock, ArrowUpRight } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { useT } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SUMUP_URL = "https://pay.sumup.com/b2c/Q5A6PDCH";

const BANK = {
  intestatario: "Roberta Barbierato",
  iban: "IT03R0366901600288765241384",
  bic: "REVOITM2",
  banca: "Revolut Bank UAB",
  indirizzo: "Via Dante 7, 20123 Milano (MI), Italy",
  bicCorrispondente: "CHASDEFX",
};

function CopyRow({ label, value, testid }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(t("pay.copied", label));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("pay.copyFail"));
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#909090]">{label}</div>
        <div className="mt-0.5 break-all font-medium text-[#1a1a1a]">{value}</div>
      </div>
      <button
        onClick={copy}
        data-testid={testid}
        aria-label={`Copy ${label}`}
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#C9A84C]/30 text-[#1a1a1a] transition-colors hover:border-[#C9A84C] hover:text-[#C9A84C]"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Method({ icon: Icon, title, subtitle, open, onToggle, testid, soon, children }) {
  const { t } = useT();
  return (
    <div className="overflow-hidden rounded-2xl border border-[#C9A84C]/25 bg-white shadow-[0_1px_3px_rgba(26,26,26,0.04)]">
      <button onClick={onToggle} data-testid={testid} className="flex w-full items-center gap-4 px-5 py-4 text-left">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#C9A84C]/25 bg-[#C9A84C]/[0.07]">
          <Icon className="h-5 w-5 text-[#C9A84C]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-serif text-xl font-medium tracking-tight text-[#1a1a1a]">{title}</span>
            {soon && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C9A84C]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#9a7d2e]">
                <Clock className="h-3 w-3" /> {t("pay.inArrivo")}
              </span>
            )}
          </div>
          {subtitle && <div className="mt-0.5 text-sm text-[#909090]">{subtitle}</div>}
        </div>
        {!soon && <ChevronDown className={`h-5 w-5 shrink-0 text-[#909090] transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && children && <div className="border-t border-[#C9A84C]/15 px-5 pb-5 pt-2">{children}</div>}
    </div>
  );
}

export default function Payment() {
  const { t } = useT();
  const [open, setOpen] = useState("bonifico");
  const [cardOpen, setCardOpen] = useState(false);
  const toggle = (k) => setOpen((cur) => (cur === k ? null : k));

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <AppHeader />

      <main className="mx-auto max-w-md px-5 pb-24 pt-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="text-center">
          <h1 className="font-serif text-[2.4rem] font-medium leading-tight text-[#1a1a1a] sm:text-5xl">{t("pay.title")}</h1>
          <p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-[#4a4a4a]">{t("pay.subtitle")}</p>
        </motion.div>

        <div className="mt-10 space-y-4">
          <Method icon={Landmark} title={t("pay.bonifico")} subtitle={t("pay.bonificoSub")} testid="method-bonifico" open={open === "bonifico"} onToggle={() => toggle("bonifico")}>
            <div className="divide-y divide-[#C9A84C]/10">
              <CopyRow label={t("pay.intestatario")} value={BANK.intestatario} testid="copy-intestatario" />
              <CopyRow label="IBAN" value={BANK.iban} testid="copy-iban" />
              <CopyRow label="BIC / SWIFT" value={BANK.bic} testid="copy-bic" />
              <div className="py-2.5">
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#909090]">{t("pay.banca")}</div>
                <div className="mt-0.5 font-medium text-[#1a1a1a]">{BANK.banca}</div>
                <div className="text-sm text-[#909090]">{BANK.indirizzo}</div>
              </div>
              <CopyRow label={t("pay.bicCorr")} value={BANK.bicCorrispondente} testid="copy-bic-corr" />
            </div>
            <p className="mt-3 rounded-xl bg-[#f9f8f5] px-4 py-3 text-xs leading-relaxed text-[#909090]">
              {t("pay.notePre")}
              <a href="/documenti" className="font-semibold text-[#1a1a1a] underline decoration-[#C9A84C] underline-offset-2">{t("pay.noteLink")}</a>.
            </p>
          </Method>

          <Method icon={CreditCard} title="PayPal" subtitle={t("pay.paypalSub")} testid="method-paypal" soon open={false} onToggle={() => toast(t("pay.paypalToast"))} />

          <button
            type="button"
            data-testid="method-carta"
            onClick={() => setCardOpen(true)}
            className="flex w-full items-center gap-4 rounded-2xl border border-[#C9A84C]/25 bg-white px-5 py-4 text-left shadow-[0_1px_3px_rgba(26,26,26,0.04)] transition-colors hover:border-[#C9A84C]/60"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#C9A84C]/25 bg-[#C9A84C]/[0.07]">
              <CreditCard className="h-5 w-5 text-[#C9A84C]" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-serif text-xl font-medium tracking-tight text-[#1a1a1a]">{t("pay.carta")}</span>
              <div className="mt-0.5 text-sm text-[#909090]">{t("pay.cartaSub")}</div>
            </div>
            <span className="shrink-0 rounded-full bg-[#1a1a1a] px-4 py-2 text-xs font-medium text-white">{t("pay.cartaPaga")}</span>
          </button>
        </div>
      </main>

      <Dialog open={cardOpen} onOpenChange={setCardOpen}>
        <DialogContent className="max-w-md gap-0 overflow-hidden bg-white p-0" data-testid="sumup-dialog">
          <DialogHeader className="border-b border-[#C9A84C]/15 px-5 py-4 text-left">
            <DialogTitle className="font-serif text-xl font-medium text-[#1a1a1a]">{t("pay.sumupTitle")}</DialogTitle>
          </DialogHeader>
          <iframe
            src={SUMUP_URL}
            title="SumUp"
            data-testid="sumup-iframe"
            className="h-[65vh] w-full border-0"
            allow="payment"
          />
          <div className="border-t border-[#C9A84C]/15 px-5 py-3 text-center text-xs text-[#909090]">
            {t("pay.sumupFallback")}
            <a href={SUMUP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-[#1a1a1a] underline decoration-[#C9A84C] underline-offset-2">
              {t("pay.sumupFallbackLink")} <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
